import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/tokens.css';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './theme';
import { CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSkeleton from './components/common/LoadingSkeleton';
import Overview from './pages/Overview';
import { UserRoleProvider } from './contexts/UserRoleContext';
import { RefreshProvider } from './contexts/RefreshContext';
import { LayoutActionsProvider } from './contexts/LayoutActionsContext';
import { SearchInterceptProvider } from './contexts/SearchInterceptContext';

// Retry dynamic imports once — if a chunk 404s after deploy, reload to get new index.html
function lazyRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      const reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      return factory(); // second attempt — let it fail naturally
    })
  );
}

const Candidates = lazyRetry(() => import('./pages/Candidates'));
const Voters = lazyRetry(() => import('./pages/Voters'));
const Elections = lazyRetry(() => import('./pages/Elections'));
const ElectionDetails = lazyRetry(() => import('./pages/ElectionDetails'));
const CandidateProfile = lazyRetry(() => import('./pages/CandidateProfile'));
const Results = lazyRetry(() => import('./pages/Results'));
const DataInsights = lazyRetry(() => import('./pages/DataInsights'));
const Vote = lazyRetry(() => import('./pages/Vote'));
const Compare = lazyRetry(() => import('./pages/Compare'));
const Manifestos = lazyRetry(() => import('./pages/Manifestos'));
const ManifestoDetails = lazyRetry(() => import('./pages/ManifestoDetails'));
const Proposals = lazyRetry(() => import('./pages/Proposals'));
const ElectionCountdown = lazyRetry(() => import('./pages/ElectionCountdown'));
const CareerIntelligencePage = lazyRetry(() => import('./pages/CareerIntelligencePage'));
const UnifiedComponentsDemo = lazyRetry(() => import('./pages/UnifiedComponentsDemo'));
const NotFound = lazyRetry(() => import('./pages/NotFound'));
const News = lazyRetry(() => import('./pages/News'));
const PoliticalActors = lazyRetry(() => import('./pages/PoliticalActors'));
const Forecast = lazyRetry(() => import('./pages/Forecast'));
const Networks = lazyRetry(() => import('./pages/Networks'));


const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSkeleton spinner />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Overview />} />
          <Route path="candidates" element={<Candidates />} />
          <Route path="manifestos" element={<Manifestos />} />
          <Route path="manifesto/:manifestoId" element={<ManifestoDetails />} />
          <Route path="proposals" element={<Proposals />} />
          <Route path="election-countdown" element={<ElectionCountdown />} />
          <Route path="candidate/:candidateId" element={<CandidateProfile />} />
          <Route path="candidate/:candidateId/career-intelligence" element={<CareerIntelligencePage />} />
          <Route path="unified-components" element={<UnifiedComponentsDemo />} />
          <Route path="voters" element={<Voters />} />
          <Route path="elections" element={<Elections />} />
          <Route path="election/:electionId" element={<ElectionDetails />} />
          <Route path="data-insights" element={<DataInsights />} />
          <Route path="analytics" element={<Navigate to="/data-insights" replace />} />
          <Route path="forecast" element={<Forecast />} />
          <Route path="networks" element={<Networks />} />
          <Route path="vote" element={<Vote />} />
          <Route path="compare" element={<Compare />} />
          <Route path="social-media" element={<Navigate to="/news" replace />} />
          <Route path="news" element={<News />} />
          <Route path="political-actors" element={<PoliticalActors />} />
          <Route path="results" element={<Results />} />
<Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <UserRoleProvider>
          <RefreshProvider>
            <LayoutActionsProvider>
              <SearchInterceptProvider>
                <Router>
                  <AppRoutes />
                </Router>
              </SearchInterceptProvider>
            </LayoutActionsProvider>
          </RefreshProvider>
        </UserRoleProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
